import {type ComponentType, lazy, type LazyExoticComponent} from 'react'

// localStorage and not sessionStorage: iOS standalone PWAs drop sessionStorage across reloads, which would
// let the recovery loop run forever. The key is shared with public/recovery.js.
const RELOAD_KEY = 'pwa:lazy-reload-at'
const RELOAD_GUARD_MS = 60_000

function reloadedRecently(): boolean {
  try {
    const raw = localStorage.getItem(RELOAD_KEY)
    if (raw === null) return false
    const at = Number(raw)
    return Number.isFinite(at) && (Date.now() - at) < RELOAD_GUARD_MS
  } catch {
    return false
  }
}

function markReloaded(): void {
  try {
    localStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    // storage may be unavailable — proceed with reload anyway
  }
}

function clearReloadMark(): void {
  try {
    localStorage.removeItem(RELOAD_KEY)
  } catch {
    // noop
  }
}

async function dropClientState(): Promise<void> {
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
  } catch {
    // best-effort cleanup
  }
  try {
    const regs = await navigator.serviceWorker?.getRegistrations?.() ?? []
    await Promise.all(regs.map((r) => r.unregister()))
  } catch {
    // best-effort cleanup
  }
}

// WebKit strips the chunk URL from its import failures, so it is recovered from the bundled factory body.
function extractChunkUrl(factory: () => unknown): string | undefined {
  const match = factory.toString().match(/import\(\s*["']([^"']+)["']\s*\)/)
  return match?.[1]
}

export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{default: T}>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await factory()
      if (!mod || typeof mod.default === 'undefined') {
        throw new Error('Lazy module resolved without a default export — likely a stale chunk')
      }
      clearReloadMark()
      return mod
    } catch (cause) {
      const chunkUrl = extractChunkUrl(factory) ?? 'unknown'
      const swControlled = typeof navigator !== 'undefined' && navigator.serviceWorker?.controller !== null
      const online = typeof navigator !== 'undefined' ? navigator.onLine : 'unknown'
      const error = new Error(
        `lazyWithReload failed — chunk=${chunkUrl} sw=${swControlled} online=${online}`,
        {cause},
      )

      // Stay suspended rather than flash an error UI before the stub's own reload fires.
      if (cause instanceof Error && cause.message === 'Stale asset — forcing refresh') {
        return new Promise<{default: T}>(() => {})
      }

      if (reloadedRecently()) {
        throw error
      }
      markReloaded()
      await dropClientState()
      window.location.reload()
      return new Promise<{default: T}>(() => {})
    }
  })
}
